import React, { useState } from 'react';
// MUI Core Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// MUI Icons
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import BugReportIcon from '@mui/icons-material/BugReport';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LinkIcon from '@mui/icons-material/Link';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import TimerIcon from '@mui/icons-material/Timer';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import InfoIcon from '@mui/icons-material/Info';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

// Third-party libraries
import { motion } from 'framer-motion';

// Local imports
import { AnalysisResult, StoredAnalysis } from '../types/analysis';
import { API_CONFIG } from '../config';

// Helper function to generate R2 URLs using the config
const getEarlyUrlsLink = (requestId: string, url: string) => {
  return API_CONFIG.EARLY_URLS_PATTERN(requestId, new URL(url).hostname);
};

const getCompleteUrlsLink = (requestId: string, url: string) => {
  return API_CONFIG.COMPLETE_URLS_PATTERN(requestId, new URL(url).hostname);
};

interface AnalysisResultsProps {
  result?: AnalysisResult | StoredAnalysis | null;
}

const MotionCard = motion(Card);
const MotionAccordion = motion(Accordion);

// Format milliseconds to readable time
const formatTime = (ms: number) => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
};

// Format date
const formatDate = (dateStr: string) => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  } catch (e) {
    return dateStr;
  }
};

// Update the copy functionality at the top level
const copyToClipboard = (content: any, format: 'markdown' | 'json' = 'markdown') => {
  let text = '';
  
  if (format === 'json') {
    text = JSON.stringify(content, null, 2);
  } else {
    // Default to markdown
    text = typeof content === 'string' 
      ? content 
      : JSON.stringify(content, null, 2);
  }
  
  navigator.clipboard.writeText(text);
};

export default function AnalysisResults({ result }: AnalysisResultsProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<string | false>('summary');

  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (!result) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Check for security-blocked sites
  const isSecurityBlocked = 
    String(result.errorCode) === '403' || 
    result.status === 'security_blocked';

  // Check for not found errors
  const isNotFound = 
    String(result.errorCode) === '404' || 
    result.status === 'not_found';

  // Check if CTA data is available
  const hasCta = result.ctaData && result.ctaData.primaryCtaUrl !== undefined && result.ctaData.primaryCtaUrl !== null;

  console.log('Error detection:', { 
    isSecurityBlocked, 
    isNotFound,
    status: result.status, 
    errorCode: result.errorCode,
    message: result.message
  });
  
  // Handle security challenge conditions
  if (isSecurityBlocked) {
    return (
      <MotionCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        elevation={0}
        sx={{ 
          borderRadius: 3,
          bgcolor: 'rgba(18, 21, 40, 0.5)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Header Section with meta information */}
        <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Typography variant="h5" component="h2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
              Analysis Results: {result.pages?.main?.url || 'Website'}
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {result.analysisTime && (
                <Chip 
                  icon={<TimerIcon />} 
                  label={`Analysis time: ${formatTime(result.analysisTime || 0)}`}
                  color="default"
                  variant="outlined"
                  sx={{ 
                    fontWeight: 500,
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }}
                />
              )}
              
              {result.requestId && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    icon={<FingerprintIcon />} 
                    label={`ID: ${result.requestId}`}
                    color="default"
                    variant="outlined"
                    sx={{ 
                      fontWeight: 500,
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    }}
                  />
                  <Tooltip title="Copy ID to clipboard">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        navigator.clipboard.writeText(result.requestId);
                      }}
                      sx={{ ml: 0.5, color: 'rgba(255, 255, 255, 0.6)' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              
              {/* Early URL Access - Add R2 link with timestamp */}
              {result.requestId && result.pages?.main?.url && (
                <Tooltip title="Access extracted URLs early in the analysis process">
                  <Link
                    href={`https://buildswarm-r2.speedppc.workers.dev/early-urls/${result.requestId}_${new URL(result.pages.main.url).hostname.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Chip 
                      icon={<LinkIcon />} 
                      label="Early URLs"
                      variant="outlined"
                      sx={{ 
                        fontWeight: 500,
                        color: '#81c784',
                        borderColor: 'rgba(129, 199, 132, 0.5)',
                        '&:hover': {
                          borderColor: '#81c784',
                          bgcolor: 'rgba(129, 199, 132, 0.08)'
                        }
                      }}
                    />
                  </Link>
                </Tooltip>
              )}
              
              {/* Complete URL Access - Add R2 link for final URLs including goodUrls */}
              {result.requestId && result.pages?.main?.url && (
                <Tooltip title="Access complete URLs including Good URLs">
                  <Link
                    href={`https://buildswarm-r2.speedppc.workers.dev/urls/${result.requestId}_${new URL(result.pages.main.url).hostname.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Chip 
                      icon={<LinkIcon />} 
                      label="Complete URLs"
                      variant="outlined"
                      sx={{ 
                        fontWeight: 500,
                        color: '#90caf9',
                        borderColor: 'rgba(144, 202, 249, 0.5)',
                        '&:hover': {
                          borderColor: '#90caf9',
                          bgcolor: 'rgba(144, 202, 249, 0.08)'
                        }
                      }}
                    />
                  </Link>
                </Tooltip>
              )}
              
              {/* Early URL Timestamp - shows how quickly URLs were available */}
              <Chip 
                icon={<AccessTimeIcon />} 
                label="URLs available in ~2s"
                color="info"
                variant="outlined"
                sx={{ 
                  fontWeight: 500,
                  color: '#64b5f6',
                  borderColor: 'rgba(100, 181, 246, 0.5)'
                }}
              />
              
              {result.cachedAt && (
                <Chip 
                  icon={<HistoryIcon />} 
                  label={formatDate(result.cachedAt)}
                  color="default"
                  variant="outlined"
                  sx={{ 
                    fontWeight: 500,
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }}
                />
              )}
              
              {result.message && (
                <Chip 
                  icon={<MessageIcon />} 
                  label={result.message}
                  color="info"
                  variant="outlined"
                  sx={{ 
                    fontWeight: 500,
                    color: 'white',
                    borderColor: 'rgba(31, 170, 232, 0.5)'
                  }}
                />
              )}
            </Box>
          </Box>
          
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => {
                const jsonStr = JSON.stringify(result, null, 2);
                const blob = new Blob([jsonStr], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `website-analysis-${result.requestId || new Date().getTime()}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Export JSON
            </Button>
          </Box>
        </Box>
        
        {/* Content section with security warning */}
        <CardContent sx={{ p: 4 }}>
          <Box 
            sx={{ 
              bgcolor: 'rgba(229, 57, 53, 0.1)', 
              borderRadius: 2,
              p: 3,
              border: '1px solid rgba(229, 57, 53, 0.3)',
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              textAlign: 'center'
            }}
          >
            <Typography variant="h5" component="h2" color="#e53935" sx={{ fontWeight: 600 }}>
              Security Challenge Detected
            </Typography>
            
            <Typography variant="body1" color="rgba(255, 255, 255, 0.7)" sx={{ maxWidth: 600 }}>
              We were unable to analyze this website as it seems to be protected by a security challenge, CAPTCHA, or anti-bot protection. 
              This often happens with sites that have strict security measures to prevent automated access.
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                color="error"
                href={result.pages?.main?.url || '#'} 
                target="_blank"
                sx={{ mr: 2 }}
              >
                Visit Website Manually
              </Button>
              
              <Button 
                variant="outlined"
                sx={{ 
                  color: 'white', 
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
                onClick={() => {
                  // Copy result to clipboard
                  const jsonStr = JSON.stringify(result, null, 2);
                  navigator.clipboard.writeText(jsonStr);
                }}
              >
                Copy Debug Data
              </Button>
            </Box>
          </Box>
        </CardContent>
      </MotionCard>
    );
  }
  
  // Handle not found errors
  if (isNotFound) {
    return (
      <MotionCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        elevation={0}
        sx={{ 
          borderRadius: 3,
          bgcolor: 'rgba(18, 21, 40, 0.5)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Header Section with meta information */}
        <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <Typography variant="h5" component="h2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
              Analysis Results: {result.pages?.main?.url || 'Website'}
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {result.analysisTime && (
                <Chip 
                  icon={<TimerIcon />} 
                  label={`Analysis time: ${formatTime(result.analysisTime || 0)}`}
                  color="default"
                  variant="outlined"
                  sx={{ 
                    fontWeight: 500,
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }}
                />
              )}
              
              {result.requestId && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    icon={<FingerprintIcon />} 
                    label={`ID: ${result.requestId}`}
                    color="default"
                    variant="outlined"
                    sx={{ 
                      fontWeight: 500,
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    }}
                  />
                  <Tooltip title="Copy ID to clipboard">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        navigator.clipboard.writeText(result.requestId);
                      }}
                      sx={{ ml: 0.5, color: 'rgba(255, 255, 255, 0.6)' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              
              {result.cachedAt && (
                <Chip 
                  icon={<HistoryIcon />} 
                  label={formatDate(result.cachedAt)}
                  color="default"
                  variant="outlined"
                  sx={{ 
                    fontWeight: 500,
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }}
                />
              )}
              
              {result.message && (
                <Chip 
                  icon={<MessageIcon />} 
                  label={result.message}
                  color="info"
                  variant="outlined"
                  sx={{ 
                    fontWeight: 500,
                    color: 'white',
                    borderColor: 'rgba(31, 170, 232, 0.5)'
                  }}
                />
              )}
            </Box>
          </Box>
          
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => {
                const jsonStr = JSON.stringify(result, null, 2);
                const blob = new Blob([jsonStr], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `website-analysis-${result.requestId || new Date().getTime()}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Export JSON
            </Button>
          </Box>
        </Box>
        
        {/* Content section with not found error */}
        <CardContent sx={{ p: 4 }}>
          <Box 
            sx={{ 
              bgcolor: 'rgba(255, 152, 0, 0.1)', 
              borderRadius: 2,
              p: 3,
              border: '1px solid rgba(255, 152, 0, 0.3)',
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              textAlign: 'center'
            }}
          >
            <Typography variant="h5" component="h2" color="#ff9800" sx={{ fontWeight: 600 }}>
              Website Not Found
            </Typography>
            
            <Typography variant="body1" color="rgba(255, 255, 255, 0.7)" sx={{ maxWidth: 600 }}>
              We were unable to access this website. The website may not exist, may be offline, or may have server issues.
              Please verify the URL and try again.
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                color="warning"
                href={result.pages?.main?.url || '#'} 
                target="_blank"
                sx={{ mr: 2 }}
              >
                Check URL Manually
              </Button>
              
              <Button 
                variant="outlined"
                sx={{ 
                  color: 'white', 
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
                onClick={() => {
                  // Copy result to clipboard
                  const jsonStr = JSON.stringify(result, null, 2);
                  navigator.clipboard.writeText(jsonStr);
                }}
              >
                Copy Debug Data
              </Button>
            </Box>
          </Box>
        </CardContent>
      </MotionCard>
    );
  }

  // Main content with accordions for normal results
  return (
    <MotionCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      elevation={0}
      sx={{ 
        borderRadius: 3,
        bgcolor: 'rgba(18, 21, 40, 0.5)',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Header Section with meta information */}
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <Typography variant="h5" component="h2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
            Analysis Results: {result.businessDetails?.name || (result.businessDetails as any)?.companyName || 'Website'}
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {result.analysisTime && (
              <Chip 
                icon={<TimerIcon />} 
                label={`Analysis time: ${formatTime(result.analysisTime || 0)}`}
                color="default"
                variant="outlined"
                sx={{ 
                  fontWeight: 500,
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                }}
              />
            )}
            
            {result.requestId && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip 
                  icon={<FingerprintIcon />} 
                  label={`ID: ${result.requestId}`}
                  color="default"
                  variant="outlined"
                  sx={{ 
                    fontWeight: 500,
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }}
                />
                <Tooltip title="Copy ID to clipboard">
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      navigator.clipboard.writeText(result.requestId);
                    }}
                    sx={{ ml: 0.5, color: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            
            {result.cachedAt && (
              <Chip 
                icon={<HistoryIcon />} 
                label={formatDate(result.cachedAt)}
                color="default"
                variant="outlined"
                sx={{ 
                  fontWeight: 500,
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                }}
              />
            )}
            
            {result.message && (
              <Chip 
                icon={<MessageIcon />} 
                label={result.message}
                color="info"
                variant="outlined"
                sx={{ 
                  fontWeight: 500,
                  color: 'white',
                  borderColor: 'rgba(31, 170, 232, 0.5)'
                }}
              />
            )}
          </Box>
        </Box>
        
        <Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => {
              const jsonStr = JSON.stringify(result, null, 2);
              const blob = new Blob([jsonStr], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `website-analysis-${result.requestId || new Date().getTime()}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            sx={{ 
              color: 'white', 
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Export JSON
          </Button>
        </Box>
      </Box>
      
      {/* Accordion sections */}
      <Box sx={{ p: 3 }}>
        {/* Overview Section */}
        <MotionAccordion 
          expanded={expanded === 'overview'} 
          onChange={handleAccordionChange('overview')}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          sx={{
            mb: 3,
            bgcolor: 'rgba(25, 30, 50, 0.3)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2,
            '&:before': {
              display: 'none',
            },
            overflow: 'hidden'
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
            sx={{ 
              borderBottom: expanded === 'overview' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
              bgcolor: 'rgba(63, 81, 181, 0.1)',
              '& .MuiAccordionSummary-content': {
                margin: '12px 0',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <InfoOutlinedIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                Overview
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Business Info */}
              <Grid item xs={12} md={6}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    height: '100%', 
                    bgcolor: 'rgba(18, 21, 40, 0.5)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                    Business Information
                  </Typography>
                  
                  <List disablePadding>
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemText 
                        primary={<Typography color="white" variant="subtitle2">Business Name</Typography>}
                        secondary={
                          <Typography color="rgba(255, 255, 255, 0.7)" variant="body2">
                            {result.businessDetails?.name || (result.businessDetails as any)?.companyName || 'Not Available'}
                          </Typography>
                        }
                      />
                    </ListItem>
                    
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemText 
                        primary={<Typography color="white" variant="subtitle2">Website</Typography>}
                        secondary={
                          <Link 
                            href={result.pages?.main?.url} 
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              color: theme.palette.primary.light,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {result.pages?.main?.url}
                          </Link>
                        }
                      />
                    </ListItem>
                    
                    {result.businessDetails?.description && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                          Description
                        </Typography>
                        <Typography variant="body2">
                          {result.businessDetails.description}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Contact Information */}
                    {(result.businessDetails?.phone || result.businessDetails?.email || result.businessDetails?.contact) && (
                      <Box sx={{ mb: 2 }}>
                        {result.businessDetails?.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ minWidth: 100, fontWeight: 600 }}>
                              Phone:
                            </Typography>
                            <Typography variant="body2">
                              {result.businessDetails.phone}
                            </Typography>
                          </Box>
                        )}
                        
                        {result.businessDetails?.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ minWidth: 100, fontWeight: 600 }}>
                              Email:
                            </Typography>
                            <Link 
                              href={`mailto:${result.businessDetails.email}`}
                              target="_blank"
                              rel="noopener"
                              sx={{ 
                                color: '#90caf9', 
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {result.businessDetails.email}
                            </Link>
                          </Box>
                        )}
                        
                        {result.businessDetails?.contact && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ minWidth: 100, fontWeight: 600 }}>
                              Contact:
                            </Typography>
                            <Typography variant="body2">
                              {result.businessDetails.contact}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </List>
                </Paper>
              </Grid>
              
              {/* Summary Information */}
              <Grid item xs={12} md={6}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    height: '100%', 
                    bgcolor: 'rgba(18, 21, 40, 0.5)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                    Summary Information
                  </Typography>
                  
                  {/* Add CTA Component */}
                  {hasCta && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'white' }}>
                        Primary Call-to-Action
                      </Typography>
                      
                      <Box sx={{ 
                        bgcolor: 'rgba(25, 30, 50, 0.5)',
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid rgba(255,255,255,0.05)'
                      }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip 
                            label={result.ctaData?.ctaType}
                            size="small"
                            sx={{ 
                              fontSize: '0.75rem',
                              backgroundColor: theme.palette.primary.main,
                              color: 'white'
                            }}
                          />
                          <Chip 
                            label={`${result.ctaData?.confidence} confidence`}
                            size="small"
                            sx={{ 
                              fontSize: '0.75rem',
                              backgroundColor: 
                                result.ctaData?.confidence === 'high' ? theme.palette.success.main : 
                                result.ctaData?.confidence === 'medium' ? theme.palette.info.main : 
                                theme.palette.warning.main,
                              color: 'white'
                            }}
                          />
                        </Box>
                        
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: 'white' }}>
                          URL:
                        </Typography>
                        <Link 
                          href={result.ctaData?.primaryCtaUrl || '#'} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ 
                            color: theme.palette.primary.light,
                            fontSize: '0.875rem',
                            display: 'block',
                            mb: 1.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'white'
                            }
                          }}
                        >
                          {result.ctaData?.primaryCtaUrl}
                        </Link>
                        
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                          Description:
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {result.ctaData?.ctaDescription}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  <List disablePadding>
                    {result.summary?.siteType && (
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemText 
                          primary={<Typography color="white" variant="subtitle2">Site Type</Typography>}
                          secondary={
                            <Typography color="rgba(255, 255, 255, 0.7)" variant="body2">
                              {result.summary?.siteType}
                            </Typography>
                          }
                        />
                      </ListItem>
                    )}
                    
                    {result.summary?.jobsToDo && (
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemText 
                          primary={<Typography color="white" variant="subtitle2">Jobs to be Done</Typography>}
                          secondary={
                            <Typography color="rgba(255, 255, 255, 0.7)" variant="body2">
                              {result.summary?.jobsToDo}
                            </Typography>
                          }
                        />
                      </ListItem>
                    )}
                    
                    {result.summary?.goal && (
                      <ListItem disablePadding sx={{ mb: 1, mt: 2 }}>
                        <ListItemText 
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography color="white" variant="subtitle2">Primary Goal</Typography>
                              <Tooltip title="Copy to clipboard">
                                <IconButton 
                                  size="small" 
                                  onClick={() => copyToClipboard(result.summary?.goal)}
                                  sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                          secondary={
                            <Typography 
                              color="white" 
                              variant="body1" 
                              sx={{ 
                                mt: 1, 
                                p: 2, 
                                borderRadius: 1, 
                                bgcolor: 'rgba(25, 30, 50, 0.5)',
                                fontStyle: 'italic',
                                borderLeft: `4px solid ${theme.palette.primary.main}`
                              }}
                            >
                              "{result.summary?.goal}"
                            </Typography>
                          }
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </MotionAccordion>
        
        {/* Features Section */}
        {result.summary?.features && result.summary?.features.length > 0 && (
          <MotionAccordion 
            expanded={expanded === 'features'} 
            onChange={handleAccordionChange('features')}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            sx={{
              mb: 3,
              bgcolor: 'rgba(25, 30, 50, 0.3)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              '&:before': {
                display: 'none',
              },
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
              sx={{ 
                borderBottom: expanded === 'features' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                bgcolor: 'rgba(63, 81, 181, 0.1)',
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <StarIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  Features & Benefits ({result.summary?.features?.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Grid container spacing={2}>
                {result.summary?.features?.map((feature, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 3,
                        height: '100%',
                        bgcolor: 'rgba(18, 21, 40, 0.5)', 
                        border: '1px solid rgba(255,255,255,0.05)',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                        <Chip 
                          label={`#${index + 1}`}
                          size="small"
                          sx={{ 
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white', flexGrow: 1 }}>
                          {feature.title}
                        </Typography>
                        <Tooltip title="Copy to clipboard">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(`${feature.title}: ${feature.description}`)}
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)',
                          flexGrow: 1
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </MotionAccordion>
        )}

        {/* Persona Section */}
        {result.summary?.personaVoice && (
          <MotionAccordion 
            expanded={expanded === 'persona'} 
            onChange={handleAccordionChange('persona')}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            sx={{
              mb: 3,
              bgcolor: 'rgba(25, 30, 50, 0.3)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              '&:before': {
                display: 'none',
              },
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
              sx={{ 
                borderBottom: expanded === 'persona' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                bgcolor: 'rgba(63, 81, 181, 0.1)',
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  AI Persona
                </Typography>
                {result.summary?.personaVoice?.personaName && (
                  <Chip 
                    label={result.summary?.personaVoice?.personaName}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Persona Voice */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      height: '100%', 
                      bgcolor: 'rgba(18, 21, 40, 0.5)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                      Persona Voice & Style
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ 
                          p: 2, 
                          height: '100%',
                          bgcolor: 'rgba(25, 30, 50, 0.5)',
                          border: '1px solid rgba(255,255,255,0.05)',
                          borderRadius: 1
                        }}>
                          <Typography variant="subtitle2" color="white" fontWeight={600}>Tone</Typography>
                          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mt: 1 }}>
                            {result.summary?.personaVoice?.tone || 'Not specified'}
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ 
                          p: 2, 
                          height: '100%',
                          bgcolor: 'rgba(25, 30, 50, 0.5)',
                          border: '1px solid rgba(255,255,255,0.05)',
                          borderRadius: 1
                        }}>
                          <Typography variant="subtitle2" color="white" fontWeight={600}>Style</Typography>
                          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mt: 1 }}>
                            {result.summary?.personaVoice?.style || 'Not specified'}
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Paper sx={{ 
                          p: 2, 
                          bgcolor: 'rgba(25, 30, 50, 0.5)',
                          border: '1px solid rgba(255,255,255,0.05)',
                          borderRadius: 1
                        }}>
                          <Typography variant="subtitle2" color="white" fontWeight={600}>Brand Personality</Typography>
                          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mt: 1 }}>
                            {result.summary?.personaVoice?.brandPersonality || 'Not specified'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                    
                    <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'white', fontWeight: 600 }}>
                      Messaging Elements
                    </Typography>
                    
                    {/* Key Phrases */}
                    {result.summary?.personaVoice?.keyPhrases && result.summary?.personaVoice?.keyPhrases.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="white" fontWeight={600} sx={{ mb: 1 }}>
                          Key Phrases
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {result.summary?.personaVoice?.keyPhrases?.map((phrase, index) => (
                            <Chip 
                              key={index}
                              label={phrase}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                color: 'white', 
                                borderColor: 'rgba(255, 63, 122, 0.5)',
                                bgcolor: 'rgba(255, 63, 122, 0.1)'
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Emotional Triggers */}
                    {result.summary?.personaVoice?.emotionalTriggers && result.summary?.personaVoice?.emotionalTriggers.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="white" fontWeight={600} sx={{ mb: 1 }}>
                          Emotional Triggers
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {result.summary?.personaVoice?.emotionalTriggers?.map((trigger, index) => (
                            <Chip 
                              key={index}
                              label={trigger}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                color: 'white', 
                                borderColor: 'rgba(63, 81, 181, 0.5)',
                                bgcolor: 'rgba(63, 81, 181, 0.1)'
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                
                {/* Instructions */}
                <Grid item xs={12} md={6}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      height: '100%', 
                      bgcolor: 'rgba(18, 21, 40, 0.5)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        AI Instructions
                      </Typography>
                      <Tooltip title="Copy to clipboard">
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(result.summary?.personaVoice?.personaInstruction)}
                          sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    {/* Persona Instructions */}
                    {result.summary?.personaVoice?.personaInstruction && (
                      <Box sx={{ mb: 3, flexGrow: 1 }}>
                        <Typography variant="subtitle2" color="white" fontWeight={600} sx={{ mb: 1 }}>
                          Detailed Persona Instructions
                        </Typography>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(25, 30, 50, 0.5)',
                            border: '1px solid rgba(255,255,255,0.05)',
                            borderRadius: 1,
                            maxHeight: 200,
                            overflow: 'auto'
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'rgba(255, 255, 255, 0.9)',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {result.summary?.personaVoice?.personaInstruction}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                    
                    {/* Role Instructions */}
                    {result.summary?.personaVoice?.roleInstruction && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" color="white" fontWeight={600}>
                            Concise Role Instructions
                          </Typography>
                          <Tooltip title="Copy to clipboard">
                            <IconButton 
                              size="small" 
                              onClick={() => copyToClipboard(result.summary?.personaVoice?.roleInstruction)}
                              sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(25, 30, 50, 0.5)',
                            border: '1px solid rgba(255,255,255,0.05)',
                            borderRadius: 1
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'rgba(255, 255, 255, 0.9)',
                              fontStyle: 'italic'
                            }}
                          >
                            "{result.summary?.personaVoice?.roleInstruction}"
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                    
                    {result.summary?.personaVoice?.rules && result.summary?.personaVoice?.rules.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" color="white" fontWeight={600}>
                            Rules
                          </Typography>
                          <Tooltip title="Copy to clipboard">
                            <IconButton 
                              size="small" 
                              onClick={() => copyToClipboard(result.summary?.personaVoice?.rules?.join('\n') || '')}
                              sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(25, 30, 50, 0.5)',
                            border: '1px solid rgba(255,255,255,0.05)',
                            borderRadius: 1
                          }}
                        >
                          <List disablePadding>
                            {result.summary?.personaVoice?.rules.map((rule, index) => (
                              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                <ListItemText 
                                  primary={
                                    <Typography color="rgba(255, 255, 255, 0.8)" variant="body2">
                                      {rule}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Box>
                    )}

                    {result.summary?.personaVoice?.discoveryQuestions && result.summary?.personaVoice?.discoveryQuestions.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                          Discovery Questions
                        </Typography>
                        <List 
                          disablePadding 
                          sx={{ 
                            bgcolor: 'rgba(25, 30, 50, 0.5)',
                            border: '1px solid rgba(255,255,255,0.05)',
                            borderRadius: 1,
                            p: 2
                          }}
                        >
                          {result.summary?.personaVoice?.discoveryQuestions?.map((question, index) => (
                            <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 30, color: theme.palette.primary.main }}>
                                <ArrowRightIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={
                                  <Typography color="rgba(255, 255, 255, 0.8)" variant="body2">
                                    {question}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </MotionAccordion>
        )}

        {/* Objections Section */}
        {result.summary?.objections && result.summary?.objections.length > 0 && (
          <MotionAccordion 
            expanded={expanded === 'objections'} 
            onChange={handleAccordionChange('objections')}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            sx={{
              mb: 3,
              bgcolor: 'rgba(25, 30, 50, 0.3)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              '&:before': {
                display: 'none',
              },
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
              sx={{ 
                borderBottom: expanded === 'objections' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                bgcolor: 'rgba(63, 81, 181, 0.1)',
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MessageIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  Objection Handling ({result.summary?.objections?.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {result.summary?.objections?.map((objection, index) => (
                  <Grid item xs={12} key={index}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 3,
                        bgcolor: 'rgba(18, 21, 40, 0.5)', 
                        border: '1px solid rgba(255,255,255,0.05)',
                        borderRadius: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                        <Chip 
                          label={`#${index + 1}`}
                          size="small"
                          sx={{ 
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white', flexGrow: 1 }}>
                          {objection.objection}
                        </Typography>
                        <Tooltip title="Copy to clipboard">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(`Objection: ${objection.objection}\n\nResponse: ${objection.response}`)}
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, mb: 1 }}>
                        Suggested Response:
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.8)',
                          p: 2,
                          borderRadius: 1,
                          bgcolor: 'rgba(25, 30, 50, 0.5)',
                          fontStyle: 'italic',
                          borderLeft: `4px solid ${theme.palette.primary.main}`
                        }}
                      >
                        "{objection.response}"
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </MotionAccordion>
        )}

        {/* Sales Insights Section */}
        {((result.summary && result.summary.painPoints && Array.isArray(result.summary.painPoints) && result.summary.painPoints.length > 0) || 
          (result.summary && result.summary.jobsToBeDone && Array.isArray(result.summary.jobsToBeDone) && result.summary.jobsToBeDone.length > 0) || 
          (result.summary && result.summary.personaCentricBenefits && Object.keys(result.summary.personaCentricBenefits).length > 0) || 
          (result.summary && result.summary.socialProof && Array.isArray(result.summary.socialProof) && result.summary.socialProof.length > 0) || 
          (result.summary && result.summary.offersAndGuarantees && Array.isArray(result.summary.offersAndGuarantees) && result.summary.offersAndGuarantees.length > 0) ||
          (result.summary && result.summary.priorityKnowledge && Array.isArray(result.summary.priorityKnowledge) && result.summary.priorityKnowledge.length > 0) ||
          (result.summary && result.summary.commitmentLevel)) && (
          <MotionAccordion 
            expanded={expanded === 'sales-insights'} 
            onChange={handleAccordionChange('sales-insights')}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
            sx={{
              mb: 3,
              bgcolor: 'rgba(25, 30, 50, 0.3)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              '&:before': {
                display: 'none',
              },
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
              sx={{ 
                borderBottom: expanded === 'sales-insights' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                bgcolor: 'rgba(63, 81, 181, 0.1)',
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <QuestionAnswerIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  Sales Insights
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Pain Points */}
                {result.summary?.painPoints && Array.isArray(result.summary.painPoints) && result.summary.painPoints.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} color="primary.light" gutterBottom>
                        Pain Points
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Tooltip title="Copy to clipboard">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(result.summary?.painPoints?.join('\n') || '')}
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <List>
                        {result.summary.painPoints.map((point, index) => (
                          <ListItem key={index} sx={{ py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <ArrowRightIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={point} primaryTypographyProps={{ color: 'white' }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Grid>
                )}

                {/* Jobs To Be Done */}
                {result.summary?.jobsToBeDone && Array.isArray(result.summary.jobsToBeDone) && result.summary.jobsToBeDone.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} color="primary.light" gutterBottom>
                        Jobs To Be Done
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Tooltip title="Copy to clipboard">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(result.summary?.jobsToBeDone?.join('\n') || '')}
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <List>
                        {result.summary.jobsToBeDone.map((job, index) => (
                          <ListItem key={index} sx={{ py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <ArrowRightIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={job} primaryTypographyProps={{ color: 'white' }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Grid>
                )}

                {/* Persona Centric Benefits */}
                {result.summary?.personaCentricBenefits && Object.keys(result.summary.personaCentricBenefits).length > 0 && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        mb: 3
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} color="primary.light" gutterBottom>
                        Persona-Centric Benefits
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Tooltip title="Copy to clipboard">
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              const text = Object.entries(result.summary?.personaCentricBenefits || {})
                                .map(([job, benefits]) => `${job}:\n${(benefits as string[]).join('\n')}`)
                                .join('\n\n');
                              copyToClipboard(text);
                            }}
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Grid container spacing={2}>
                        {Object.entries(result.summary.personaCentricBenefits).map(([jobType, benefits], index) => (
                          <Grid item xs={12} md={6} key={index}>
                            <Paper 
                              elevation={0} 
                              sx={{ 
                                p: 2,
                                bgcolor: 'rgba(25, 30, 50, 0.5)',
                                border: '1px solid rgba(255,255,255,0.05)',
                                borderRadius: 1
                              }}
                            >
                              <Typography variant="subtitle2" color="white" fontWeight={600} sx={{ mb: 1 }}>
                                {jobType.charAt(0).toUpperCase() + jobType.slice(1)}
                              </Typography>
                              <List dense>
                                {Array.isArray(benefits) && benefits.map((benefit, i) => (
                                  <ListItem key={i} sx={{ py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                      <ArrowRightIcon fontSize="small" color="primary" />
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={benefit} 
                                      primaryTypographyProps={{ 
                                        variant: 'body2', 
                                        color: 'rgba(255, 255, 255, 0.9)' 
                                      }} 
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Grid>
                )}

                {/* Social Proof */}
                {result.summary?.socialProof && Array.isArray(result.summary.socialProof) && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} color="primary.light" gutterBottom>
                        Social Proof
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Tooltip title="Copy to clipboard">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(result.summary?.socialProof
                              ?.filter(proof => proof !== "NA")
                              .join('\n') || '')}
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {result.summary.socialProof.length > 0 && 
                       !result.summary.socialProof.some(proof => proof === "NA" || proof.toLowerCase().includes("no social proof")) ? (
                        <List>
                          {result.summary.socialProof
                            .filter(proof => proof !== "NA")
                            .map((proof, index) => (
                              <ListItem key={index} sx={{ py: 1 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <ArrowRightIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={proof} primaryTypographyProps={{ color: 'white' }} />
                              </ListItem>
                            ))}
                        </List>
                      ) : (
                        <Typography color="rgba(255, 255, 255, 0.6)" sx={{ mt: 1, fontStyle: 'italic' }}>
                          N/A
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Offers and Guarantees */}
                {result.summary?.offersAndGuarantees && Array.isArray(result.summary.offersAndGuarantees) && result.summary.offersAndGuarantees.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} color="primary.light" gutterBottom>
                        Offers & Guarantees
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Tooltip title="Copy to clipboard">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(result.summary?.offersAndGuarantees
                              ?.filter(offer => offer !== "NA")
                              .join('\n') || '')}
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {result.summary.offersAndGuarantees.length > 0 && 
                       !result.summary.offersAndGuarantees.some(offer => offer === "NA") ? (
                        <List>
                          {result.summary.offersAndGuarantees
                            .filter(offer => offer !== "NA")
                            .map((offer, index) => (
                              <ListItem key={index} sx={{ py: 1 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <LocalOfferIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={offer} primaryTypographyProps={{ color: 'white' }} />
                              </ListItem>
                            ))}
                        </List>
                      ) : (
                        <Typography color="rgba(255, 255, 255, 0.6)" sx={{ mt: 1, fontStyle: 'italic' }}>
                          N/A
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Priority Knowledge */}
                {result.summary?.priorityKnowledge && Array.isArray(result.summary.priorityKnowledge) && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} color="primary.light" gutterBottom>
                        Priority Knowledge
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Tooltip title="Copy to clipboard">
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(result.summary?.priorityKnowledge
                              ?.filter(item => item !== "NA" && !item.includes("no "))
                              .join('\n') || '')}
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {result.summary.priorityKnowledge.length > 0 && 
                       !result.summary.priorityKnowledge.some(item => item === "NA" || item.includes("no ")) ? (
                        <List>
                          {result.summary.priorityKnowledge
                            .filter(item => item !== "NA" && !item.includes("no "))
                            .map((item, index) => (
                              <ListItem key={index} sx={{ py: 1 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <InfoIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={item} primaryTypographyProps={{ color: 'white' }} />
                              </ListItem>
                            ))}
                        </List>
                      ) : (
                        <Typography color="rgba(255, 255, 255, 0.6)" sx={{ mt: 1, fontStyle: 'italic' }}>
                          N/A
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Commitment Level */}
                {result.summary?.commitmentLevel && (
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} color="primary.light" gutterBottom>
                        Commitment Level
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Chip
                          label={result.summary.commitmentLevel}
                          color={result.summary.commitmentLevel.includes("low") ? "success" : "error"}
                          sx={{ 
                            textTransform: 'capitalize',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </MotionAccordion>
        )}

        {/* Website Section */}
        <MotionAccordion 
          expanded={expanded === 'website'} 
          onChange={handleAccordionChange('website')}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          sx={{
            mb: 3,
            bgcolor: 'rgba(25, 30, 50, 0.3)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2,
            '&:before': {
              display: 'none',
            },
            overflow: 'hidden'
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
            sx={{ 
              borderBottom: expanded === 'website' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
              bgcolor: 'rgba(63, 81, 181, 0.1)',
              '& .MuiAccordionSummary-content': {
                margin: '12px 0',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LanguageIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                Website Pages
              </Typography>
              <Chip 
                label={`${1 + (result.pages?.secondary?.length || 0)} Pages`}
                size="small"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Visual Identity Section */}
              {(result.favicon || (result.colorScheme && Object.keys(result.colorScheme).length > 0)) && (
                <Grid item xs={12}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      mb: 3,
                      bgcolor: 'rgba(18, 21, 40, 0.5)', 
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 3, color: 'white', fontWeight: 600 }}>
                      Visual Identity
                    </Typography>
                    
                    <Grid container spacing={3}>
                      {/* Favicon */}
                      {result.favicon && (
                        <Grid item xs={12} md={6}>
                          <Paper 
                            elevation={0} 
                            sx={{ 
                              p: 3, 
                              height: '100%',
                              bgcolor: 'rgba(25, 30, 50, 0.3)',
                              border: '1px solid rgba(255,255,255,0.05)',
                              borderRadius: 2,
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                              Favicon
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Paper 
                                elevation={0} 
                                sx={{ 
                                  p: 2, 
                                  mr: 2,
                                  bgcolor: 'white',
                                  borderRadius: 1,
                                  width: 64,
                                  height: 64,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <img 
                                  src={result.favicon.storedUrl || result.favicon.url} 
                                  alt="Favicon" 
                                  style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: '100%',
                                    display: 'block'
                                  }}
                                />
                              </Paper>
                              
                              <Box>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                  Type: {result.favicon.type || 'Unknown'}
                                </Typography>
                                {result.favicon.sizes && (
                                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                    Sizes: {result.favicon.sizes}
                                  </Typography>
                                )}
                                {result.favicon.highRes && (
                                  <Chip
                                    label="High Resolution"
                                    size="small"
                                    color="success"
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </Box>
                            </Box>
                            
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                              Original URL:
                            </Typography>
                            <Link 
                              href={result.favicon.originalUrl} 
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ 
                                color: theme.palette.primary.light,
                                mb: 1,
                                fontSize: '0.8rem',
                                wordBreak: 'break-all',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {result.favicon.originalUrl}
                            </Link>
                            
                            {result.favicon.storedUrl && (
                              <>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1, display: 'block' }}>
                                  R2 Stored URL:
                                </Typography>
                                <Link 
                                  href={result.favicon.storedUrl} 
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ 
                                    color: theme.palette.primary.light,
                                    fontSize: '0.8rem',
                                    wordBreak: 'break-all',
                                    textDecoration: 'none',
                                    '&:hover': {
                                      textDecoration: 'underline'
                                    }
                                  }}
                                >
                                  {result.favicon.storedUrl}
                                </Link>
                              </>
                            )}
                          </Paper>
                        </Grid>
                      )}
                      
                      {/* Color Scheme */}
                      {result.colorScheme && Object.keys(result.colorScheme).length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Paper 
                            elevation={0} 
                            sx={{ 
                              p: 3, 
                              height: '100%',
                              bgcolor: 'rgba(25, 30, 50, 0.3)',
                              border: '1px solid rgba(255,255,255,0.05)',
                              borderRadius: 2,
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                                Color Scheme
                              </Typography>
                              {result.colorScheme.isDarkMode !== undefined && (
                                <Chip
                                  label={result.colorScheme.isDarkMode ? "Dark Mode" : "Light Mode"}
                                  size="small"
                                  color={result.colorScheme.isDarkMode ? "default" : "primary"}
                                  sx={{ fontWeight: 500 }}
                                />
                              )}
                            </Box>
                            
                            {/* Color Demo */}
                            <Box sx={{ mb: 3, p: 2, borderRadius: 1, bgcolor: result.colorScheme.backgroundColor }}>
                              <Typography variant="caption" sx={{ color: result.colorScheme.textColor, display: 'block', mb: 1 }}>
                                Button Preview:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box 
                                  sx={{ 
                                    bgcolor: result.colorScheme.primaryButtonColor,
                                    color: result.colorScheme.primaryButtonTextColor,
                                    border: `1px solid ${result.colorScheme.primaryButtonBorderColor}`,
                                    borderRadius: '4px',
                                    py: 0.7,
                                    px: 2,
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    display: 'inline-block'
                                  }}
                                >
                                  Primary Button
                                </Box>
                                <Box 
                                  sx={{ 
                                    bgcolor: result.colorScheme.secondaryButtonColor,
                                    color: result.colorScheme.secondaryButtonTextColor,
                                    border: `1px solid ${result.colorScheme.secondaryButtonBorderColor}`,
                                    borderRadius: '4px',
                                    py: 0.7,
                                    px: 2,
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    display: 'inline-block'
                                  }}
                                >
                                  Secondary Button
                                </Box>
                              </Box>
                            </Box>
                            
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              {/* Site Colors */}
                              <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                  Site Colors
                                </Typography>
                              </Grid>
                              
                              {result.colorScheme.backgroundColor && (
                                <Grid item xs={6} sm={4}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                      Background
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Box 
                                        sx={{ 
                                          width: 24, 
                                          height: 24, 
                                          borderRadius: '4px',
                                          mr: 1,
                                          bgcolor: result.colorScheme.backgroundColor,
                                          border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }} 
                                      />
                                      <Typography variant="body2" sx={{ color: 'white' }}>
                                        {result.colorScheme.backgroundColor}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}
                              
                              {result.colorScheme.textColor && (
                                <Grid item xs={6} sm={4}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                      Text
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Box 
                                        sx={{ 
                                          width: 24, 
                                          height: 24, 
                                          borderRadius: '4px',
                                          mr: 1,
                                          bgcolor: result.colorScheme.textColor,
                                          border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }} 
                                      />
                                      <Typography variant="body2" sx={{ color: 'white' }}>
                                        {result.colorScheme.textColor}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}
                              
                              {/* Primary Button Colors */}
                              <Grid item xs={12} sx={{ mt: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                  Primary Button
                                </Typography>
                              </Grid>
                              
                              {result.colorScheme.primaryButtonColor && (
                                <Grid item xs={6} sm={4}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                      Background
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Box 
                                        sx={{ 
                                          width: 24, 
                                          height: 24, 
                                          borderRadius: '4px',
                                          mr: 1,
                                          bgcolor: result.colorScheme.primaryButtonColor,
                                          border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }} 
                                      />
                                      <Typography variant="body2" sx={{ color: 'white' }}>
                                        {result.colorScheme.primaryButtonColor}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}
                              
                              {result.colorScheme.primaryButtonTextColor && (
                                <Grid item xs={6} sm={4}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                      Text
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Box 
                                        sx={{ 
                                          width: 24, 
                                          height: 24, 
                                          borderRadius: '4px',
                                          mr: 1,
                                          bgcolor: result.colorScheme.primaryButtonTextColor,
                                          border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }} 
                                      />
                                      <Typography variant="body2" sx={{ color: 'white' }}>
                                        {result.colorScheme.primaryButtonTextColor}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}
                              
                              {result.colorScheme.primaryButtonBorderColor && (
                                <Grid item xs={6} sm={4}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                      Border
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Box 
                                        sx={{ 
                                          width: 24, 
                                          height: 24, 
                                          borderRadius: '4px',
                                          mr: 1,
                                          bgcolor: result.colorScheme.primaryButtonBorderColor,
                                          border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }} 
                                      />
                                      <Typography variant="body2" sx={{ color: 'white' }}>
                                        {result.colorScheme.primaryButtonBorderColor}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}
                              
                              {/* Secondary Button Colors */}
                              <Grid item xs={12} sx={{ mt: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                  Secondary Button
                                </Typography>
                              </Grid>
                              
                              {result.colorScheme.secondaryButtonColor && (
                                <Grid item xs={6} sm={4}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                      Background
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Box 
                                        sx={{ 
                                          width: 24, 
                                          height: 24, 
                                          borderRadius: '4px',
                                          mr: 1,
                                          bgcolor: result.colorScheme.secondaryButtonColor,
                                          border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }} 
                                      />
                                      <Typography variant="body2" sx={{ color: 'white' }}>
                                        {result.colorScheme.secondaryButtonColor}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}
                              
                              {result.colorScheme.secondaryButtonTextColor && (
                                <Grid item xs={6} sm={4}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                      Text
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Box 
                                        sx={{ 
                                          width: 24, 
                                          height: 24, 
                                          borderRadius: '4px',
                                          mr: 1,
                                          bgcolor: result.colorScheme.secondaryButtonTextColor,
                                          border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }} 
                                      />
                                      <Typography variant="body2" sx={{ color: 'white' }}>
                                        {result.colorScheme.secondaryButtonTextColor}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}
                              
                              {result.colorScheme.secondaryButtonBorderColor && (
                                <Grid item xs={6} sm={4}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                      Border
                                    </Typography>
                                    <Box sx={{ 
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      <Box 
                                        sx={{ 
                                          width: 24, 
                                          height: 24, 
                                          borderRadius: '4px',
                                          mr: 1,
                                          bgcolor: result.colorScheme.secondaryButtonBorderColor,
                                          border: '1px solid rgba(255, 255, 255, 0.1)'
                                        }} 
                                      />
                                      <Typography variant="body2" sx={{ color: 'white' }}>
                                        {result.colorScheme.secondaryButtonBorderColor}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                            
                            {result.colorScheme.chatWidgetStatus && (
                              <Box sx={{ mt: 'auto' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                  Chat Widget Status
                                </Typography>
                                <Chip
                                  label={result.colorScheme.chatWidgetStatus === 'liveChatActive' ? 'Live Chat Active' : result.colorScheme.chatWidgetStatus}
                                  size="small"
                                  color="success"
                                  sx={{ mt: 0.5, textTransform: 'capitalize' }}
                                />
                              </Box>
                            )}
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}
              
              {/* Main Page */}
              <Grid item xs={12}>
                <Accordion 
                  elevation={0}
                  sx={{
                    mb: 2,
                    overflow: 'hidden',
                    bgcolor: 'rgba(25, 30, 50, 0.5)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    '&:before': {
                      display: 'none',
                    }
                  }}
                  defaultExpanded={true}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
                    sx={{ 
                      bgcolor: 'rgba(63, 81, 181, 0.1)',
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LanguageIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
                        {result.pages?.main?.url} <Chip size="small" label="Main Page" color="primary" sx={{ ml: 1 }} />
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    {/* Main Page Text Preview */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                        Page Content Preview
                      </Typography>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'rgba(25, 30, 50, 0.3)',
                          borderRadius: 1,
                          maxHeight: 150,
                          overflowY: 'auto'
                        }}
                      >
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                          {result.pages?.main?.text ? (result.pages.main.text.length > 300 ? `${result.pages.main.text.substring(0, 300)}...` : result.pages.main.text) : 'No text content available'}
                        </Typography>
                      </Paper>
                    </Box>
                    
                    {/* Main Page Images */}
                    {result.pages?.main?.images && result.pages.main.images.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                          Page Screenshot
                        </Typography>
                        <Box sx={{ 
                          p: 2, 
                          bgcolor: 'rgba(25, 30, 50, 0.3)',
                          borderRadius: 2,
                          border: '1px solid rgba(255,255,255,0.05)',
                        }}>
                          <a 
                            href={result.pages?.main?.images[0].url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <img 
                              src={result.pages?.main?.images[0].url} 
                              alt={`Screenshot of ${result.pages?.main?.url}`} 
                              style={{ 
                                maxWidth: '100%', 
                                height: 'auto', 
                                borderRadius: '8px',
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                              }}
                            />
                          </a>
                        </Box>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Secondary Pages */}
              {result.pages?.secondary?.map((page: any, pageIndex: number) => (
                <Grid item xs={12} key={pageIndex}>
                  <Accordion 
                    elevation={0}
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      bgcolor: 'rgba(25, 30, 50, 0.5)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 2,
                      '&:before': {
                        display: 'none',
                      }
                    }}
                    defaultExpanded={false}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
                      sx={{ 
                        bgcolor: 'rgba(255, 63, 122, 0.05)',
                        '& .MuiAccordionSummary-content': {
                          margin: '12px 0',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LanguageIcon sx={{ color: theme.palette.primary.light }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
                          {page.url}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                      {/* Page Text Preview */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                          Page Content Preview
                        </Typography>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(25, 30, 50, 0.3)',
                            borderRadius: 1,
                            maxHeight: 150,
                            overflowY: 'auto'
                          }}
                        >
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {page.text ? (page.text.length > 300 ? `${page.text.substring(0, 300)}...` : page.text) : 'No text content available'}
                          </Typography>
                        </Paper>
                      </Box>
                      
                      {/* Page Images */}
                      {page.images && page.images.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                            Page Screenshot
                          </Typography>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: 'rgba(25, 30, 50, 0.3)',
                            borderRadius: 2,
                            border: '1px solid rgba(255,255,255,0.05)',
                          }}>
                            <a 
                              href={page.images[0].url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <img 
                                src={page.images[0].url} 
                                alt={`Screenshot of ${page.url}`} 
                                style={{ 
                                  maxWidth: '100%', 
                                  height: 'auto', 
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                                }}
                              />
                            </a>
                          </Box>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
              
              {/* Important Links */}
              {((result.links && result.links.length > 0) || (result.goodUrls && result.goodUrls.length > 0)) && (
                <Grid item xs={12}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      bgcolor: 'rgba(18, 21, 40, 0.5)', 
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                      Important Links
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {((result.links || []).concat(result.goodUrls || [])).map((link, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Paper 
                            elevation={0} 
                            sx={{ 
                              p: 2, 
                              height: '100%',
                              bgcolor: 'rgba(25, 30, 50, 0.3)',
                              border: '1px solid rgba(255,255,255,0.05)',
                              borderRadius: 1,
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                              {link.title || 'Link ' + (index + 1)}
                            </Typography>
                            
                            <Link 
                              href={link.url} 
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ 
                                color: theme.palette.primary.light,
                                mb: 1,
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {link.url.length > 50 ? `${link.url.substring(0, 50)}...` : link.url}
                            </Link>
                            
                            {link.description && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'rgba(255, 255, 255, 0.7)',
                                }}
                              >
                                {link.description.length > 100 ? `${link.description.substring(0, 100)}...` : link.description}
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </MotionAccordion>

        {/* Debug Section */}
        <MotionAccordion 
          expanded={expanded === 'debug'} 
          onChange={handleAccordionChange('debug')}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
          sx={{
            mb: 3,
            bgcolor: 'rgba(25, 30, 50, 0.3)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2,
            '&:before': {
              display: 'none',
            },
            overflow: 'hidden'
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
            sx={{ 
              borderBottom: expanded === 'debug' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
              bgcolor: 'rgba(63, 81, 181, 0.1)',
              '& .MuiAccordionSummary-content': {
                margin: '12px 0',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BugReportIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                Debug Information
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Process Logs */}
              {result.processLogs && result.processLogs.length > 0 && (
                <Grid item xs={12}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      mb: 3,
                      bgcolor: 'rgba(18, 21, 40, 0.5)', 
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Process Logs
                      </Typography>
                      <Tooltip title="Copy to clipboard">
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(result.processLogs)}
                          sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        bgcolor: 'rgba(25, 30, 50, 0.3)',
                        border: '1px solid rgba(255,255,255,0.05)',
                        borderRadius: 1,
                        p: 2,
                        maxHeight: 300,
                        overflow: 'auto'
                      }}
                    >
                      <List disablePadding>
                        {result.processLogs.map((log: any, index: number) => (
                          <ListItem 
                            key={index}
                            disableGutters
                            disablePadding
                            sx={{ 
                              mb: 1,
                              borderRadius: 1,
                              bgcolor: 
                                log.type === 'error' ? 'rgba(229, 57, 53, 0.1)' :
                                log.type === 'warning' ? 'rgba(255, 152, 0, 0.1)' :
                                log.type === 'success' ? 'rgba(76, 175, 80, 0.1)' :
                                'transparent',
                              border: 
                                log.type === 'error' ? '1px solid rgba(229, 57, 53, 0.3)' :
                                log.type === 'warning' ? '1px solid rgba(255, 152, 0, 0.3)' :
                                log.type === 'success' ? '1px solid rgba(76, 175, 80, 0.3)' :
                                'none',
                              p: 1
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 1 }}>
                                  <Typography 
                                    variant="caption" 
                                    component="span" 
                                    sx={{ 
                                      color: 'rgba(255, 255, 255, 0.6)',
                                      minWidth: 80
                                    }}
                                  >
                                    {new Date(log.timestamp).toLocaleTimeString()}
                                  </Typography>
                                  <Chip 
                                    label={log.type}
                                    size="small"
                                    sx={{ 
                                      minWidth: 70,
                                      height: 20,
                                      fontSize: '0.7rem',
                                      bgcolor: 
                                        log.type === 'error' ? '#e53935' :
                                        log.type === 'warning' ? '#ff9800' :
                                        log.type === 'success' ? '#4caf50' :
                                        '#2196f3',
                                      color: 'white'
                                    }}
                                  />
                                  <Typography 
                                    variant="body2" 
                                    component="span" 
                                    sx={{ 
                                      color: 
                                        log.type === 'error' ? '#e53935' :
                                        log.type === 'warning' ? '#ff9800' :
                                        log.type === 'success' ? '#4caf50' :
                                        'rgba(255, 255, 255, 0.9)',
                                    }}
                                  >
                                    {log.message}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Paper>
                </Grid>
              )}
              
              {/* URL Data Section - Early URLs and Complete URLs */}
              {result.requestId && result.pages?.main?.url && (
                <Grid item xs={12}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      mb: 3,
                      bgcolor: 'rgba(18, 21, 40, 0.5)', 
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        URL Data
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Access early-stage URLs collected during analysis">
                          <Link
                            href={getEarlyUrlsLink(result.requestId, result.pages.main.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: 'none' }}
                          >
                            <Chip 
                              icon={<LinkIcon />} 
                              label="Early URLs"
                              variant="outlined"
                              sx={{ 
                                fontWeight: 500,
                                color: '#81c784',
                                borderColor: 'rgba(129, 199, 132, 0.5)',
                                '&:hover': {
                                  borderColor: '#81c784',
                                  bgcolor: 'rgba(129, 199, 132, 0.08)'
                                }
                              }}
                            />
                          </Link>
                        </Tooltip>
                        
                        <Tooltip title="Access complete URLs including Good URLs">
                          <Link
                            href={getCompleteUrlsLink(result.requestId, result.pages.main.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: 'none' }}
                          >
                            <Chip 
                              icon={<LinkIcon />} 
                              label="Complete URLs"
                              variant="outlined"
                              sx={{ 
                                fontWeight: 500,
                                color: '#90caf9',
                                borderColor: 'rgba(144, 202, 249, 0.5)',
                                '&:hover': {
                                  borderColor: '#90caf9',
                                  bgcolor: 'rgba(144, 202, 249, 0.08)'
                                }
                              }}
                            />
                          </Link>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                      URLs are extracted early in the analysis process and saved to R2 storage. You can view both the early URLs (available ~2 seconds after analysis begins) and the complete URLs (including Good URLs identified during analysis).
                    </Typography>
                    
                    <Chip 
                      icon={<AccessTimeIcon />} 
                      label="URLs available in ~2s"
                      color="info"
                      variant="outlined"
                      sx={{ 
                        mb: 2,
                        fontWeight: 500,
                        color: '#64b5f6',
                        borderColor: 'rgba(100, 181, 246, 0.5)'
                      }}
                    />
                  </Paper>
                </Grid>
              )}
              
              {/* Raw Data */}
              <Grid item xs={12}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    bgcolor: 'rgba(18, 21, 40, 0.5)', 
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                      Raw JSON Data
                    </Typography>
                    <Box>
                      <Tooltip title="Copy to clipboard">
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(result, 'json')}
                          sx={{ color: 'rgba(255, 255, 255, 0.6)', mr: 1 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          const jsonStr = JSON.stringify(result, null, 2);
                          const blob = new Blob([jsonStr], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `website-analysis-${result.requestId || new Date().getTime()}.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                        sx={{ 
                          color: 'white', 
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            borderColor: 'white',
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }
                        }}
                      >
                        Download JSON
                      </Button>
                    </Box>
                  </Box>
                  
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      bgcolor: 'rgba(25, 30, 50, 0.3)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 1,
                      p: 2,
                      maxHeight: 400,
                      overflow: 'auto'
                    }}
                  >
                    <pre style={{ 
                      margin: 0, 
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '0.8rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </Paper>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </MotionAccordion>
      </Box>
    </MotionCard>
  );
}